import {Card} from "../../generic/card";
import {AsRoute} from "../../../app";
import {Order, Row, Table} from "../../generic/table";
import {WithAPI} from "../../../api/temperature";
import {Button, ButtonList, IconButton} from "../../generic/button";
import {mdiClose, mdiPencil, mdiPlus} from "@mdi/js";
import {useRef, useState} from "preact/hooks";
import {useFetchAndRender} from "../../fetchAndRender";
import {Dialog, DialogClose, DialogContent, DialogOptions, useDialog} from "../../generic/dialog";
import {FormFooter, FormGroup} from "../../generic/form";
import {Checkbox} from "../../generic/checkbox";
import {Sensor, SensorOrder} from "../../../api/sensor";
import {useAlert} from "../../generic/alert";
import {NewSensorSet, SensorSet, SensorSetOrder} from "../../../api/set";
import {useLoader} from "../../generic/loader";
import {useFlash} from "../../generic/flash";
import {submitForm} from "../../../lib/submitForm";

export type SensorSetDialogProps<T> = {
    sensorSet?: SensorSet;
    onSubmit: ((sensorSet: T) => boolean | Promise<boolean>);
} & DialogOptions & WithAPI;

export function SensorSetDialog<T extends NewSensorSet>({api, sensorSet, onSubmit, ...props}: SensorSetDialogProps<T>) {
    const [sensors] = useFetchAndRender(async () => {
        return (await api.sensor.get({filter: {active: undefined}})).sensors
    }, {interval: 0})

    const [fullSensorSet] = useFetchAndRender<SensorSet | null, [SensorSet | undefined]>(async () => {
        if (!sensorSet) {
            return null;
        }

        return (await api.sets.get({
            filter: {ids: [sensorSet.id]},
            attributes: ["id", "name", "sensors"]
        })).sensor_sets[0]
    }, {interval: 0}, [sensorSet]);

    if (!sensors || (sensorSet && !fullSensorSet)) {
        return null;
    }

    const form = useRef<HTMLFormElement>(null);
    const selectedSensors = fullSensorSet?.sensors?.map(sensor => sensor.id) ?? [];

    return <Dialog {...props}>
        <DialogContent>
            <Card
                title={sensorSet ? `Modify sensor set ${sensorSet.name}` : "Create sensor set"}
                footer={<FormFooter>
                    <Button onClick={() => form.current && submitForm(form.current)}>{sensorSet ? "Modify" : "Create"}</Button>
                    <DialogClose>Cancel</DialogClose>
                </FormFooter>}
            >
                <form onSubmit={(e) => {
                    e.preventDefault();

                    const fd = new FormData(e.currentTarget);
                    const newSensorSet: T = {
                        name: fd.get("name") as string
                    } as T;

                    newSensorSet.sensors = fd.getAll("sensor").map(
                        sensorId => {
                            return {id: Number.parseInt(sensorId as string)} as Sensor
                        }
                    );

                    if (sensorSet) {
                        (newSensorSet as unknown as SensorSet).id = sensorSet.id;
                    }

                    if (onSubmit(newSensorSet)) {
                        props.onOpenChange(false);
                    }
                }} ref={form}>
                    <FormGroup>
                        <label for="input--name">Name:</label>
                        <input type="text" name="name" id="input--name" required value={fullSensorSet?.name} />
                    </FormGroup>

                    <FormGroup>
                        <legend>Sensors:</legend>
                        {sensors.map(sensor =>
                            <Checkbox
                                name="sensor"
                                value={sensor.id}
                                label={sensor.name}
                                checked={selectedSensors.indexOf(sensor.id) >= 0}
                            />)}
                    </FormGroup>
                </form>
            </Card>
        </DialogContent>
    </Dialog>
}

export function CreateSensorSet({api, ...props}: WithAPI & DialogOptions) {
    const {success} = useFlash();

    return <SensorSetDialog<NewSensorSet>
        api={api}
        {...props}
        onSubmit={async (newSensorSet) => {
            await api.sets.create(newSensorSet);
            success(`Sensor set ${newSensorSet.name} has been created.`);
            return true;
        }} />;
}

export function ModifySensorSet({api, sensorSet, ...props}: WithAPI & {sensorSet: SensorSet} & DialogOptions) {
    const {success} = useFlash();

    return <SensorSetDialog<SensorSet>
        api={api}
        sensorSet={sensorSet}
        {...props}
        onSubmit={async (modifySensorSet) => {
            await api.sets.update({
                id: sensorSet.id,
                name: modifySensorSet.name,
                sensors: modifySensorSet.sensors,
                sensors_update_mode: "overwrite",
            });
            success(`Sensor set ${sensorSet.name} has been modified.`);
            return true;
        }} />;
}

export function SensorSetsAdmin(props: AsRoute & WithAPI) {
    const [order, setOrder] = useState<Order<"id" | "name">>({
        column: "name",
        direction: "ASC"
    });

    const [sets, forceReload] = useFetchAndRender(async (order) => {
        return (await props.api.sets.get({
            order: order ? [{attribute: order.column, direction: order.direction}] : undefined
        })).sensor_sets;
    }, {interval: 0}, [order])

    const [showCreateSensorSet, setShowCreateSensorSet] = useState<boolean>(false);
    const [showModifySensorSet, setShowModifySensorSet] = useState<SensorSet | null>(null);

    const {showConfirm} = useAlert();
    const [showLoader, hideLoader] = useLoader();
    const {success} = useFlash();

    return <Card title={"Sensor sets"}>
        <Table
            columns={[
                {id: "id", title: "ID", order: true, class: "td-shrink"},
                {id: "name", title: "Sensor set", order: true},
                {id: "actions", title: <ButtonList className="text-right">
                        <IconButton
                            path={mdiPlus}
                            className="btn"
                            onClick={() => {
                                setShowCreateSensorSet(true);
                            }}
                        />
                </ButtonList>, class: "td-shrink text-right"}
            ]}
            onOrdered={(order) => {
                if (order.column !== "actions") {
                    setOrder(order as Order<SensorSetOrder["attribute"]>);
                }
            }}
            order={order}
        >{(sets ?? []).map(set =>
            <Row cells={{
                "id": <td class="text-right">{set.id}</td>,
                "name": <th>{set.name}</th>,
                "actions": <td class="td-shrink"><ButtonList>
                    <IconButton path={mdiPencil} onClick={() => {
                        setShowModifySensorSet(set);
                    }} className="btn" />
                    <IconButton path={mdiClose} onClick={() => {
                        showConfirm("Remove sensor set?", `Do you really want to remove sensor set ${set.name}?`)
                            .then((res) => {
                                if (res === "yes") {
                                    showLoader(`Removing sensor set ${set.name}...`);
                                    props.api.sets.remove(set.id).then(() => {
                                        success(`Sensor set ${set.name} has been removed.`);
                                        forceReload(true);
                                    }).finally(() => { hideLoader(); });
                                }
                            });
                    }} className="btn" />
                </ButtonList></td>
            }} />
        )}</Table>

        {showCreateSensorSet && <CreateSensorSet
            api={props.api}
            open={true}
            onOpenChange={(show) => {
                if (!show) {
                    setShowCreateSensorSet(false);
                }
            }
        } />}

        {showModifySensorSet && <ModifySensorSet
            api={props.api}
            sensorSet={showModifySensorSet}
            open={true}
            onOpenChange={(show) => {
                if (!show) {
                    setShowModifySensorSet(null);
                }
            }
        } />}
    </Card>;
}
